/**
 * Image compression utility for handling large images from mobile cameras
 * Used by ReceiptScanModal
 *
 * Compresses images to fit within a target file size by:
 * 1. Resizing dimensions if very large (mobile camera photos)
 * 2. Iteratively reducing JPEG quality until under target size
 * 3. Converting to JPEG format for optimal compression
 *
 * @param {File} file - The image file to compress
 * @param {number} maxSizeMB - Maximum file size in megabytes (default: 5MB)
 * @returns {Promise<File>} - Compressed image file
 */
export const compressImage = (file, maxSizeMB = 5) => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      const img = new Image();
      img.onload = () => {
        const canvas = document.createElement('canvas');
        let width = img.width;
        let height = img.height;

        // For very large images (mobile camera), be more aggressive with initial resize
        const maxDimension = width * height > 4000000 ? 1600 : 2048;

        if (width > height && width > maxDimension) {
          height = (height / width) * maxDimension;
          width = maxDimension;
        } else if (height > maxDimension) {
          width = (width / height) * maxDimension;
          height = maxDimension;
        }

        canvas.width = width;
        canvas.height = height;

        const ctx = canvas.getContext('2d');
        ctx.drawImage(img, 0, 0, width, height);

        // Try different quality settings to get under maxSizeMB
        let quality = 0.85;
        const tryCompress = () => {
          canvas.toBlob((blob) => {
            if (!blob) {
              reject(new Error('Failed to compress image'));
              return;
            }

            if (blob.size > maxSizeMB * 1024 * 1024 && quality > 0.3) {
              quality -= 0.1;
              tryCompress();
            } else if (blob.size > maxSizeMB * 1024 * 1024) {
              reject(new Error(`Unable to compress image below ${maxSizeMB}MB. Final size: ${(blob.size / 1024 / 1024).toFixed(2)}MB`));
            } else {
              const compressedFile = new File([blob], file.name.replace(/\.[^/.]+$/, '.jpg'), {
                type: 'image/jpeg',
                lastModified: Date.now()
              });
              resolve(compressedFile);
            }
          }, 'image/jpeg', quality);
        };

        tryCompress();
      };
      img.onerror = () => reject(new Error('Failed to load image'));
      img.src = e.target.result;
    };
    reader.onerror = () => reject(new Error('Failed to read file'));
    reader.readAsDataURL(file);
  });
};
